/**
 * Formato di gioco e tipi degli eventi.
 *
 * Tutto il pacchetto e composto da funzioni pure: nessuna I/O, nessuna
 * lettura di `Date.now()` all'interno. L'istante corrente si passa sempre
 * come parametro. Serve a tre cose:
 *
 *  - il server puo rivalidare un minuto inserito a mano usando le stesse
 *    funzioni del client;
 *  - il client puo applicare lo scarto di orologio rilevato tramite
 *    l'heartbeat senza che la logica lo sappia;
 *  - i test coprono il recupero e i casi limite senza orologi finti.
 */

export interface Formato {
  nPeriodi: number;
  durataPeriodoMin: number;
  giocatoriInCampo: number;
  maxSostituzioni: number;
  rientroConsentito: boolean;
}

export type TipoEvento =
  | 'passaggio_completato'
  | 'passaggio_sbagliato'
  | 'palla_conquistata'
  | 'palla_persa'
  | 'tiro_specchio'
  | 'tiro_fuori'
  | 'gol'
  | 'autogol'
  | 'assist'
  | 'cartellino_giallo'
  | 'cartellino_rosso'
  | 'ingresso'
  | 'uscita'
  | 'gol_subito'
  | 'autogol_avversario'
  | 'inizio_periodo'
  | 'fine_periodo';

export const EVENTI_SENZA_GIOCATORE: readonly TipoEvento[] = [
  'gol_subito',
  'autogol_avversario',
  'inizio_periodo',
  'fine_periodo',
];

export interface Evento {
  id: string;
  tipo: TipoEvento;
  giocatoreId: string | null;
  /** Periodo di gioco, a partire da 1. */
  periodo: number;
  /**
   * Minuto cumulativo sull'intera partita, a base 1: il primo minuto di
   * gioco e il minuto 1. Il secondo tempo di una 2x45 parte dal 46.
   * Il valore 0 e riservato agli ingressi dei titolari al fischio d'inizio.
   */
  minuto: number;
  secondo: number;
  /** Istante reale, ISO 8601. Dirime l'ordine a parita di minuto. */
  orarioReale: string;
  /**
   * Cancellazione logica. Il client tiene in memoria anche gli eventi
   * cancellati, perche l'elenco a fondo pagina li mostra barrati e permette
   * di ripristinarli; ogni calcolo li esclude.
   */
  cancellatoIl?: string | null;
}

/** Filtro obbligatorio prima di qualunque calcolo. */
export const eventiAttivi = (eventi: readonly Evento[]): Evento[] =>
  eventi.filter((e) => !e.cancellatoIl);

export interface Sospensione {
  periodo: number;
  orarioInizio: string;
  orarioFine: string | null;
}

/** Minuto in cui il periodo inizia a contare. Periodo 1 -> 0. */
export const offsetPeriodo = (periodo: number, f: Formato): number =>
  (periodo - 1) * f.durataPeriodoMin;

/** Ultimo minuto regolamentare del periodo, recupero escluso. */
export const fineRegolamentare = (periodo: number, f: Formato): number =>
  periodo * f.durataPeriodoMin;

export const durataRegolamentare = (f: Formato): number => f.nPeriodi * f.durataPeriodoMin;

/**
 * Notazione del minuto. Oltre il tempo regolamentare si passa alla forma
 * `45+2`, dove il numero di sinistra e sempre la fine configurata del
 * periodo: nel secondo tempo di una 2x45 diventa `90+2`.
 *
 * E' presentazione, non dato: memorizzare la stringa renderebbe impossibile
 * ordinare e sommare.
 */
export function formattaMinuto(minuto: number, periodo: number, f: Formato): string {
  const fine = fineRegolamentare(periodo, f);
  return minuto <= fine ? `${minuto}'` : `${fine}+${minuto - fine}'`;
}

export function formattaOrologio(minuto: number, secondo: number): string {
  return `${minuto}:${String(secondo).padStart(2, '0')}`;
}
